/**
 * @fileoverview One module card of the Capabilities screen.
 * @description A titled card holding a table with one row (CapabilityRow) per permission in a group
 * (e.g. "User Management") and one column per role, Super Admin's marked "Fixed".
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import CapabilityRow from '@/features/capabilities/components/CapabilityRow'
import { groupTitleKey } from '@/features/capabilities/utils/capability-label.utils'

import type {
  CapabilityDraft,
  CapabilityGroup,
  CapabilityRole,
} from '@/features/capabilities/types/capabilities.types'
import type { TFunction } from 'i18next'

interface CapabilityGroupCardProps {
  group: CapabilityGroup
  roles: CapabilityRole[]
  /** The edit draft, or null when the page isn't in edit mode. */
  draft: CapabilityDraft | null
  onToggle: (roleCode: string, permissionCode: string) => void
}

/**
 * @description Writes a group's title in the viewer's language, falling back to the group key in
 * words for a group engineering added after the locale files were written.
 * @param {TFunction} t - The translate function.
 * @param {string} groupKey - The group key, e.g. "SALES_CRM".
 * @returns {string} The title.
 */
function groupTitle(t: TFunction, groupKey: string): string {
  const words = groupKey.toLowerCase().replace(/_/g, ' ')
  return t(groupTitleKey(groupKey), {
    defaultValue: words.charAt(0).toUpperCase() + words.slice(1),
  })
}

/**
 * @component
 * @param {{ role: CapabilityRole }} props - The role this column is for.
 * @param {CapabilityRole} props.role - The role; Super Admin's is marked "Fixed".
 */
function RoleHeader({ role }: { role: CapabilityRole }) {
  const { t } = useTranslation()

  return (
    <th
      scope="col"
      data-testid={`capability-role-header-${role.code}`}
      className="px-2.5 py-2.5 text-center text-[12px] font-semibold text-table-header"
    >
      {role.name}
      {role.locked && (
        <span
          className="mt-0.5 flex items-center justify-center gap-1 text-[10.5px] font-medium text-footer"
          data-testid={`capability-role-fixed-${role.code}`}
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 3l8 3v6c0 4.5-3.2 8-8 9-4.8-1-8-4.5-8-9V6l8-3z" />
          </svg>
          {t('capabilities.table.fixed')}
        </span>
      )}
    </th>
  )
}

/**
 * @component
 * @param {CapabilityGroupCardProps} props - The group, the roles, and the edit state.
 * @param {CapabilityGroup} props.group - The permissions to list.
 * @param {CapabilityRole[]} props.roles - The role columns.
 * @param {CapabilityDraft | null} props.draft - The edit draft, or null when not editing.
 * @param {(roleCode: string, permissionCode: string) => void} props.onToggle - Called when an
 * editable cell is clicked.
 */
function CapabilityGroupCard({ group, roles, draft, onToggle }: CapabilityGroupCardProps) {
  const { t } = useTranslation()

  return (
    <section
      className="rounded-[10px] border border-divider bg-white"
      data-testid={`capability-group-${group.key}`}
    >
      <div className="flex flex-wrap items-baseline gap-3 px-4 pt-4 pb-3">
        <h2
          className="text-[15px] font-bold text-ink"
          data-testid={`capability-group-title-${group.key}`}
        >
          {groupTitle(t, group.key)}
        </h2>
        <span className="text-xs text-slate" data-testid={`capability-group-count-${group.key}`}>
          {t('capabilities.group.permissionCount', { count: group.permissions.length })}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table
          className="w-full min-w-[640px] table-fixed border-collapse text-left"
          data-testid={`capability-table-${group.key}`}
        >
          <thead>
            <tr className="border-y border-divider bg-input-bg">
              <th
                scope="col"
                className="w-[40%] px-4 py-2.5 text-[12px] font-semibold text-table-header"
                data-testid="capability-permission-header"
              >
                {t('capabilities.table.whatTheyCanDo')}
              </th>
              {roles.map((role) => (
                <RoleHeader key={role.code} role={role} />
              ))}
            </tr>
          </thead>
          <tbody>
            {group.permissions.map((permission) => (
              <CapabilityRow
                key={permission.code}
                permission={permission}
                roles={roles}
                draft={draft}
                onToggle={onToggle}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export default CapabilityGroupCard
